import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import SubmitButton from '../common/SubmitButton.jsx';
import { createLicenseFormFields } from '../../config/form-fields.js';
import ErrorMessage from '../common/ErrorMessage.jsx';
import SuccessMessage from '../common/SuccessMessage.jsx';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import createLicenseValidation from '../../validation/create-ts-plus-license-validations.js';
import useTsPlusLicenses from '../../hooks/useTsPlusLicenses.js';
import useManualTsPlusLicenses from '../../hooks/useManualTsPlusLicenses.js';
import useComputers from '../../hooks/useComputers.js';

// helpers
const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(dt.getUTCDate()).padStart(2, '0');
    const yyyy = dt.getUTCFullYear();
    return `${mm}/${dd}/${yyyy}`;
};
const isClientEligible = (license) => {
    return license?.status === 'AVAILABLE';
};

/**
 * AddTsPlusLicenseToComputerForm component for rendering the form to add a ts plus license to a computer.
 *
 * @component
 * @returns {JSX.Element} - The rendered AddTsPlusLicenseToComputerForm component.
 */
const AddTsPlusLicenseToComputerForm = ({ computer }) => {
    const navigate = useNavigate();
    const computerId = computer?._id;

    const [selectedOption, setSelectedOption] = useState(null);
    const [licenseDropdownOptions, setLicenseDropdownOptions] = useState([]);
    const [createNewLicenseActive, setCreateNewLicenseActive] = useState(false);
    const [refreshAvailableLicenses, setRefreshAvailableLicenses] = useState(0);

    const {
        error: licensesError,
        loading: licensesLoading,
        getOldestAvailableLicenses,
        createLicense,
        updateLicense,
        license,
        getLicense,
        reset: licensesReset
    } = useTsPlusLicenses();

    const {
        getAvailableManualLicensesForOffice,
        assignManualLicenseToComputer,
        loading: manualLoading,
        error: manualError
    } = useManualTsPlusLicenses();

    const {
        error: computerError,
        loading: computerLoading,
        reset: computersReset
    } = useComputers();

    const formik = useFormik({
        initialValues: { ...createLicenseFormFields, computerId },
        validationSchema: createLicenseValidation,
        onSubmit: async (values) => {
            const newLicense = await createLicense(values);
            if (newLicense?._id) {
                await updateLicense(newLicense._id, { computerId });
                navigate(`/office/${computer.officeId}`);
            }
        }
    });

    useEffect(() => {
        const getLicenses = async () => {
            licensesReset();
            computersReset();

            // AUTO licenses
            let autoLicenses = await getOldestAvailableLicenses();
            if (!Array.isArray(autoLicenses)) autoLicenses = [];
            autoLicenses = autoLicenses.filter(isClientEligible);

            // MANUAL licenses (office-scoped)
            let manualLicenses = await getAvailableManualLicensesForOffice(
                computer.officeId
            );
            if (!Array.isArray(manualLicenses)) manualLicenses = [];

            const mapped = [
                ...autoLicenses.map((l) => ({
                    value: l._id || l.id,
                    label: `AUTO • ${l.license}`,
                    type: 'AUTO',
                    meta: l
                })),
                ...manualLicenses.map((l) => ({
                    value: l._id,
                    label: `MANUAL • ${l.license}`,
                    type: 'MANUAL',
                    meta: l
                }))
            ];

            setLicenseDropdownOptions(mapped);
            setSelectedOption(mapped.length ? mapped[0] : null);
        };

        if (!createNewLicenseActive) {
            getLicenses();
        }
    }, [refreshAvailableLicenses, createNewLicenseActive]);

    useEffect(() => {
        const getLicenseDetails = async () => {
            licensesReset();
            computersReset();
            formik.resetForm();
            // Only AUTO licenses need details
            if (selectedOption?.type === 'AUTO') {
                await getLicense(selectedOption.value);
            }
        };

        if (selectedOption) {
            getLicenseDetails();
        }
    }, [selectedOption]);

    const handleClickLeft = () => {
        if (createNewLicenseActive) {
            setCreateNewLicenseActive(false);
            setSelectedOption(null);
            setRefreshAvailableLicenses((v) => v + 1);
            licensesReset();
            computersReset();
            formik.resetForm();
        }
    };

    const handleClickRight = () => {
        if (!createNewLicenseActive) {
            setCreateNewLicenseActive(true);
            setSelectedOption(null);
            licensesReset();
            computersReset();
            formik.resetForm();
        }
    };

    const canAddSelected =
        selectedOption &&
        selectedOption.meta &&
        isClientEligible(selectedOption.meta);

    return (
        <>
            {/* Toggle button for add vs create process */}
            <div
                style={{
                    display: 'flex',
                    width: '50%',
                    height: '40px',
                    marginBottom: '2em',
                    marginLeft: '30px',
                    marginTop: '2em'
                }}>
                <div
                    className={
                        !createNewLicenseActive
                            ? 'reporting-query-selector-active'
                            : 'reporting-query-selector-inactive'
                    }
                    onClick={handleClickLeft}
                    id="reporting-query-selector-left">
                    <p
                        style={{
                            color: !createNewLicenseActive ? 'white' : '#2361DC'
                        }}>
                        Add available license
                    </p>
                </div>
                <div
                    className={
                        createNewLicenseActive
                            ? 'reporting-query-selector-active'
                            : 'reporting-query-selector-inactive'
                    }
                    id="reporting-query-selector-right"
                    onClick={handleClickRight}>
                    <p
                        style={{
                            color: createNewLicenseActive ? 'white' : '#2361DC'
                        }}>
                        Create new license
                    </p>
                </div>
            </div>

            {/* Add existing license */}
            {!createNewLicenseActive && (
                <>
                    <div className="newsc-form-container">
                        <h4>
                            Select from Available licenses (rehosted ≥ 6 months
                            ago)
                        </h4>

                        {/* <h4>
                            This is autofilled with the oldest available license
                            with at least 2 weeks of use left
                        </h4> */}
                        <p className="form-label">Select an existing license</p>
                        <Select
                            id="selectLicense"
                            name="selectLicense"
                            options={licenseDropdownOptions}
                            value={selectedOption}
                            onChange={setSelectedOption}
                        />
                    </div>
                    <br />
                    <br />
                    {(licensesLoading || manualLoading || computerLoading) && (
                        <LoadingSpinner />
                    )}
                    {(licensesError || manualError || computerError) && (
                        <ErrorMessage
                            message={
                                licensesError?.message ||
                                manualError?.message ||
                                computerError?.message
                            }
                        />
                    )}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            width: '100%',
                            height: 'fit-content'
                        }}>
                        {licenseDropdownOptions.length < 1 && (
                            <p style={{ margin: '1em' }}>
                                No available licenses. Please create a new one.
                            </p>
                        )}
                        {selectedOption && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '50%',
                                    height: 'fit-content',
                                    justifyContent: 'flex-start',
                                    alignContent: 'left'
                                }}>
                                <form
                                    onSubmit={async (e) => {
                                        e.preventDefault();
                                        if (!canAddSelected) return;
                                        if (selectedOption.type === 'AUTO') {
                                            await updateLicense(
                                                selectedOption.value,
                                                {
                                                    computerId
                                                }
                                            );
                                        }

                                        if (selectedOption.type === 'MANUAL') {
                                            await assignManualLicenseToComputer(
                                                selectedOption.value,
                                                computerId
                                            );
                                        }

                                        navigate(
                                            `/office/${computer.officeId}`
                                        );
                                    }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            margin: '1em',
                                            alignItems: 'center',
                                            justifyContent: 'left'
                                        }}>
                                        <table className="office-table">
                                            <tbody>
                                                <tr>
                                                    <td className="office-table-left-cell">
                                                        License:
                                                    </td>
                                                    <td className="office-table-right-cell">
                                                        <span
                                                            style={{
                                                                marginRight:
                                                                    '0.5em'
                                                            }}>
                                                            {
                                                                selectedOption
                                                                    .meta
                                                                    .license
                                                            }
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="copy-button"
                                                            onClick={() =>
                                                                navigator.clipboard.writeText(
                                                                    selectedOption
                                                                        .meta
                                                                        .license
                                                                )
                                                            }>
                                                            Copy
                                                        </button>
                                                        {/* Optional: show status if your API now derives it */}
                                                        {selectedOption.meta
                                                            .status && (
                                                            <span
                                                                style={{
                                                                    marginLeft: 8,
                                                                    background:
                                                                        '#0ea5e9',
                                                                    color: '#fff',
                                                                    padding:
                                                                        '2px 8px',
                                                                    borderRadius: 9999,
                                                                    fontSize: 12,
                                                                    fontWeight: 600
                                                                }}>
                                                                {
                                                                    selectedOption
                                                                        .meta
                                                                        .status
                                                                }
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="office-table-left-cell">
                                                        # of Users:
                                                    </td>
                                                    <td className="office-table-right-cell">
                                                        {
                                                            selectedOption.meta
                                                                .numOfUsers
                                                        }
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="office-table-left-cell">
                                                        Create Date:
                                                    </td>
                                                    <td className="office-table-right-cell">
                                                        {/* fallback to old startDate if createDate absent (until backfill done) */}
                                                        {formatDate(
                                                            selectedOption.meta
                                                                .createDate
                                                        )}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="office-table-left-cell">
                                                        Rehost Date:
                                                    </td>
                                                    <td className="office-table-right-cell">
                                                        {selectedOption.meta
                                                            .rehostDate
                                                            ? formatDate(
                                                                  selectedOption
                                                                      .meta
                                                                      .rehostDate
                                                              )
                                                            : ''}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        {!canAddSelected && (
                                            <p
                                                style={{
                                                    color: '#ef4444',
                                                    marginTop: 8
                                                }}>
                                                This license isn’t eligible
                                                (must be AVAILABLE, unassigned,
                                                and not rehosted in the last 6
                                                months).
                                            </p>
                                        )}
                                        <SubmitButton
                                            label="Add"
                                            style={{
                                                width: '5em'
                                            }}
                                            disabled={!canAddSelected}
                                        />
                                    </div>
                                </form>
                            </div>
                        )}
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '50%',
                                height: 'fit-content',
                                justifyContent: 'flex-start',
                                alignContent: 'left'
                            }}>
                            {(licensesLoading || manualLoading) && (
                                <LoadingSpinner />
                            )}

                            {licensesError && (
                                <ErrorMessage message={licensesError.message} />
                            )}

                            {manualError && (
                                <ErrorMessage message={manualError.message} />
                            )}

                            {computerLoading && <LoadingSpinner />}

                            {computerError && (
                                <ErrorMessage message={computerError.message} />
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Create new license */}
            {createNewLicenseActive && (
                <>
                    <form onSubmit={formik.handleSubmit}>
                        <div className="newsc-form-container">
                            <label htmlFor="license" className="form-label">
                                License
                            </label>
                            <input
                                id="license"
                                name="license"
                                type="text"
                                className="newsc-input"
                                placeholder="e.g. XXXX-XXXX-XXXX-XXXX"
                                {...formik.getFieldProps('license')}
                            />
                            {formik.touched.license &&
                                formik.errors.license && (
                                    <ErrorMessage
                                        message={formik.errors.license}
                                    />
                                )}

                            <label htmlFor="numOfUsers" className="form-label">
                                # of Users
                            </label>
                            <input
                                id="numOfUsers"
                                name="numOfUsers"
                                type="number"
                                className="newsc-input"
                                {...formik.getFieldProps('numOfUsers')}
                            />
                            {formik.touched.numOfUsers &&
                                formik.errors.numOfUsers && (
                                    <ErrorMessage
                                        message={formik.errors.numOfUsers}
                                    />
                                )}

                            <label htmlFor="createDate" className="form-label">
                                Create Date
                            </label>
                            <input
                                id="createDate"
                                name="createDate"
                                type="date"
                                className="newsc-input"
                                {...formik.getFieldProps('createDate')}
                            />
                            {formik.touched.createDate &&
                                formik.errors.createDate && (
                                    <ErrorMessage
                                        message={formik.errors.createDate}
                                    />
                                )}
                            <SubmitButton label="Save and add" />
                        </div>
                    </form>
                    <br />
                    <br />
                    {licensesLoading && <LoadingSpinner />}
                    {computerLoading && <LoadingSpinner />}
                    {licensesError && (
                        <ErrorMessage message={licensesError.message} />
                    )}
                    {computerError && (
                        <ErrorMessage message={computerError.message} />
                    )}
                    {license && computer && (
                        <SuccessMessage message="New license created and added successfully!" />
                    )}
                </>
            )}
        </>
    );
};

AddTsPlusLicenseToComputerForm.propTypes = {
    computerId: PropTypes.string.isRequired,
    computer: PropTypes.object.isRequired,
    officeSysName: PropTypes.string.isRequired
};

export default AddTsPlusLicenseToComputerForm;
